import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { BackPill } from './BackPill';
import { ChevronLeft, Play, Pause, RotateCcw, Volume2, VolumeX, Flag, Clock, Layers, Zap, List, Cloud } from 'lucide-react';
import { useHistoryClose } from '../../hooks/useHistoryClose';
import { getAudioCtx } from '../../utils/audioCore';

/* ═══════════════════════════════════════════════════════════════════
   THE BLISS TIMER v2 — Obsidian Chronograph
   Dual-mode premium chronograph with 4 timing modes:
     · Stopwatch    · Countdown    · Interval (Tabata)    · Pomodoro
   Plus: Laps, Session Log, Ambient backdrops, Streak ring,
   Milestone announcements, Focus Score.
   Zero-cloud-cost. Web Audio for all sound. LocalStorage for state.
   ═══════════════════════════════════════════════════════════════════ */

// ── Palette
const GOLD      = '#D4AF37';
const GOLD_HI   = '#FFE9A0';
const GOLD_LO   = '#8B6B1F';
const OBSIDIAN  = '#050308';
const NAVY_HI   = '#0E0E14';

// The 8 iconic pillar colours used throughout the app — reused here to
// tint facet ticks, mode pills, progress arcs, and the streak arc so the
// chronograph feels like it belongs to the same imperial universe.
const PILLARS = ['#D4AF37', '#C8A95E', '#0F52BA', '#DC143C', '#10B981', '#E5C44D', '#8B5CF6', '#2A9BD8'];
const PILLAR_GOLD    = PILLARS[0];
const PILLAR_TAN     = PILLARS[1];
const PILLAR_SAPPH   = PILLARS[2];
const PILLAR_CRIMSON = PILLARS[3];
const PILLAR_EMERALD = PILLARS[4];
const PILLAR_YELLOW  = PILLARS[5];
const PILLAR_VIOLET  = PILLARS[6];
const PILLAR_CYAN    = PILLARS[7];

const LS_LOG    = 'blisstimer_log_v2';
const LS_STREAK = 'blisstimer_streak_v2';   // { last: 'YYYY-MM-DD', chain: n }
const LS_LAST   = 'blisstimer_last_v2';     // last mode & settings
const LS_SCORE  = 'blisstimer_focus_score_v2';

const todayKey = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}-${String(d.getDate()).padStart(2,'0')}`;
};

const readLS = (k, d) => { try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; } };
const writeLS = (k, v) => { try { localStorage.setItem(k, JSON.stringify(v)); } catch { /* noop */ } };

const fmt = (ms) => {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${String(m).padStart(2, '0')}:${String(r).padStart(2, '0')}`;
};
const fmtDeci = (ms) => {
  const total = Math.max(0, Math.floor(ms / 10));
  const cs = total % 100;
  const s = Math.floor(total / 100) % 60;
  const m = Math.floor(total / 6000);
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}.${String(cs).padStart(2, '0')}`;
};

// ══ Audio Engine ═════════════════════════════════════════════════════
class ChronographAudio {
  constructor() { this.ctx = null; this.master = null; this.ambientNodes = []; this.muted = false; }
  _ensure() {
    if (this.ctx && this.master) { if (this.ctx.state !== 'running') { try { this.ctx.resume(); } catch { /* noop */ } } return; }
    try {
      this.ctx = getAudioCtx();
      if (!this.ctx) return;
      this.master = this.ctx.createGain();
      this.master.gain.value = this.muted ? 0 : 1;
      this.master.connect(this.ctx.destination);
    } catch { /* silent */ }
  }
  setMuted(v) { this.muted = v; if (this.master) this.master.gain.value = v ? 0 : 1; }

  // Elegant start chime — soft gold shimmer
  startChime() {
    this._ensure(); if (!this.ctx) return;
    const now = this.ctx.currentTime;
    [1046.5, 1318.5, 1568.0].forEach((f, i) => {
      const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
      o.type = 'sine'; o.frequency.setValueAtTime(f, now + i * 0.06);
      g.gain.setValueAtTime(0.0001, now + i * 0.06);
      g.gain.exponentialRampToValueAtTime(0.12, now + i * 0.06 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.06 + 0.9);
      o.connect(g); g.connect(this.master);
      o.start(now + i * 0.06); o.stop(now + i * 0.06 + 1.0);
    });
  }
  // Crystal ping for stop
  stopChime() {
    this._ensure(); if (!this.ctx) return;
    const now = this.ctx.currentTime;
    const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
    o.type = 'triangle'; o.frequency.setValueAtTime(1760, now);
    o.frequency.exponentialRampToValueAtTime(880, now + 0.25);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.18, now + 0.01);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.6);
    o.connect(g); g.connect(this.master);
    o.start(now); o.stop(now + 0.7);
  }
  // Subtle tick
  tick() {
    this._ensure(); if (!this.ctx) return;
    const now = this.ctx.currentTime;
    const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
    o.type = 'sine'; o.frequency.setValueAtTime(2600, now);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.exponentialRampToValueAtTime(0.025, now + 0.005);
    g.gain.exponentialRampToValueAtTime(0.0001, now + 0.05);
    o.connect(g); g.connect(this.master);
    o.start(now); o.stop(now + 0.08);
  }
  // Milestone bloom — soft double-chime
  milestone() {
    this._ensure(); if (!this.ctx) return;
    const now = this.ctx.currentTime;
    [1174.7, 1568.0].forEach((f, i) => {
      const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
      o.type = 'sine'; o.frequency.setValueAtTime(f, now + i * 0.15);
      g.gain.setValueAtTime(0.0001, now + i * 0.15);
      g.gain.exponentialRampToValueAtTime(0.09, now + i * 0.15 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.15 + 0.6);
      o.connect(g); g.connect(this.master);
      o.start(now + i * 0.15); o.stop(now + i * 0.15 + 0.7);
    });
  }
  // Elegant fanfare on completion (single crystal bell + slow shimmer)
  fanfare() {
    this._ensure(); if (!this.ctx) return;
    const now = this.ctx.currentTime;
    [523.25, 659.25, 783.99, 1046.5, 1318.5].forEach((f, i) => {
      const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
      o.type = 'sine'; o.frequency.setValueAtTime(f, now + i * 0.18);
      g.gain.setValueAtTime(0.0001, now + i * 0.18);
      g.gain.exponentialRampToValueAtTime(0.16, now + i * 0.18 + 0.02);
      g.gain.exponentialRampToValueAtTime(0.0001, now + i * 0.18 + 1.2);
      o.connect(g); g.connect(this.master);
      o.start(now + i * 0.18); o.stop(now + i * 0.18 + 1.3);
    });
  }

  // Ambient loops (procedural)
  stopAmbient() {
    for (const n of this.ambientNodes) { try { n.stop && n.stop(); } catch { /* noop */ } try { n.disconnect(); } catch { /* noop */ } }
    this.ambientNodes = [];
  }
  playAmbient(kind) {
    this._ensure(); if (!this.ctx) return;
    this.stopAmbient();
    if (kind === 'silence' || !kind) return;
    // ── ENERGY: procedural sports beat — kick + hi-hat pattern @ 120 BPM.
    //  Uses scheduled oscillators looping every 2 beats so it feels like
    //  a sports intro track without any audio file. Great for HIIT / focus.
    if (kind === 'energy') {
      const bpm = 120;
      const beat = 60 / bpm; // seconds per beat
      let step = 0;
      const scheduler = setInterval(() => {
        if (!this.ctx) return;
        const t0 = this.ctx.currentTime + 0.05;
        // Kick on beats 0 and 2 of each 4-beat bar
        const kick = (t) => {
          const o = this.ctx.createOscillator(); const g = this.ctx.createGain();
          o.type = 'sine'; o.frequency.setValueAtTime(140, t);
          o.frequency.exponentialRampToValueAtTime(50, t + 0.18);
          g.gain.setValueAtTime(0.0001, t);
          g.gain.exponentialRampToValueAtTime(0.28, t + 0.005);
          g.gain.exponentialRampToValueAtTime(0.0001, t + 0.22);
          o.connect(g); g.connect(this.master);
          o.start(t); o.stop(t + 0.24);
        };
        // Hi-hat (noise burst) on every 8th note
        const hat = (t) => {
          if (!this._hatBuf) {
            const bufSize = 0.05 * this.ctx.sampleRate;
            this._hatBuf = this.ctx.createBuffer(1, bufSize, this.ctx.sampleRate);
            const d = this._hatBuf.getChannelData(0);
            for (let i = 0; i < bufSize; i++) d[i] = (Math.random() * 2 - 1) * 0.6;
          }
          const src = this.ctx.createBufferSource(); src.buffer = this._hatBuf;
          const f = this.ctx.createBiquadFilter(); f.type = 'highpass'; f.frequency.value = 6000;
          const g = this.ctx.createGain();
          g.gain.setValueAtTime(0.0001, t);
          g.gain.exponentialRampToValueAtTime(0.12, t + 0.002);
          g.gain.exponentialRampToValueAtTime(0.0001, t + 0.06);
          src.connect(f); f.connect(g); g.connect(this.master);
          src.start(t); src.stop(t + 0.08);
        };
        // Snare on beats 1 and 3
        const snare = (t) => {
          if (!this._snareBuf) {
            const bufSize = 0.12 * this.ctx.sampleRate;
            this._snareBuf = this.ctx.createBuffer(1, bufSize, this.ctx.sampleRate);
            const d = this._snareBuf.getChannelData(0);
            for (let i = 0; i < bufSize; i++) d[i] = (Math.random() * 2 - 1) * (1 - i/bufSize) * 0.5;
          }
          const src = this.ctx.createBufferSource(); src.buffer = this._snareBuf;
          const f = this.ctx.createBiquadFilter(); f.type = 'bandpass'; f.frequency.value = 2000; f.Q.value = 1.4;
          const g = this.ctx.createGain(); g.gain.value = 0.22;
          src.connect(f); f.connect(g); g.connect(this.master);
          src.start(t); src.stop(t + 0.15);
        };
        // Schedule 1 bar (4 beats)
        for (let b = 0; b < 4; b++) {
          const t = t0 + b * beat;
          if (b === 0 || b === 2) kick(t);
          if (b === 1 || b === 3) snare(t);
          hat(t); hat(t + beat / 2);
        }
        step++;
      }, beat * 4 * 1000); // one bar
      this.ambientNodes = [{ stop: () => clearInterval(scheduler), disconnect: () => {} }];
      return;
    }
    // Noise buffer
    const buf = this.ctx.createBuffer(1, this.ctx.sampleRate * 4, this.ctx.sampleRate);
    const d = buf.getChannelData(0);
    if (kind === 'ocean') {
      let last = 0;
      for (let i = 0; i < d.length; i++) {
        const w = (Math.random() * 2 - 1) * 0.35;
        last = (last + w) * 0.988;
        d[i] = last * (0.6 + 0.4 * Math.sin(i / 25000));
      }
    } else if (kind === 'rain') {
      for (let i = 0; i < d.length; i++) d[i] = (Math.random() * 2 - 1) * 0.6;
    } else if (kind === 'cafe') {
      let last = 0;
      for (let i = 0; i < d.length; i++) {
        const w = (Math.random() * 2 - 1) * 0.25;
        last = (last + w) * 0.97;
        d[i] = last;
      }
    } else if (kind === 'bonfire') {
      let last = 0;
      for (let i = 0; i < d.length; i++) {
        const w = (Math.random() * 2 - 1) * 0.5;
        last = (last + w) * 0.994;
        d[i] = last + (Math.random() > 0.9995 ? (Math.random() - 0.5) * 0.6 : 0);
      }
    }
    const src = this.ctx.createBufferSource();
    src.buffer = buf; src.loop = true;
    const filt = this.ctx.createBiquadFilter();
    if (kind === 'rain')     { filt.type = 'highpass';  filt.frequency.value = 2400; }
    else if (kind === 'ocean'){ filt.type = 'lowpass';   filt.frequency.value = 500; }
    else if (kind === 'cafe') { filt.type = 'bandpass';  filt.frequency.value = 800; filt.Q.value = 0.6; }
    else if (kind === 'bonfire'){ filt.type = 'lowpass'; filt.frequency.value = 1200; }
    const g = this.ctx.createGain(); g.gain.value = 0.12;
    src.connect(filt); filt.connect(g); g.connect(this.master);
    src.start(); this.ambientNodes = [src, g];
  }

  destroy() { this.stopAmbient(); try { this.master && this.master.disconnect(); } catch { /* noop */ } this.ctx = null; this.master = null; }
}

// ══ Chronograph Ring (SVG) ═══════════════════════════════════════════
const ChronographRing = ({ size = 300, progress = 0, phaseColor = GOLD, streakArc = 0, running = false, children }) => {
  const cx = size / 2;
  const cy = size / 2;
  const rOuter = size * 0.45;
  const rInner = size * 0.36;
  const circ = 2 * Math.PI * rInner;
  const strokeDash = circ * (1 - Math.min(1, Math.max(0, progress)));
  // Streak ring (thin outer ring, dashed)
  const rStreak = size * 0.48;
  const streakCirc = 2 * Math.PI * rStreak;
  const streakDash = streakCirc * (1 - Math.min(1, Math.max(0, streakArc)));

  // Facet ticks — 60 tick marks around the ring. Major ticks (every 5th)
  // cycle through the 8 iconic pillar colours so the ring visually belongs
  // to the same universe as BLISS WORLD and the profile ring.
  const ticks = useMemo(() => {
    const arr = [];
    for (let i = 0; i < 60; i++) {
      const angle = (i / 60) * Math.PI * 2 - Math.PI / 2;
      const isMajor = i % 5 === 0;
      const rA = rOuter + (isMajor ? -1 : 1);
      const rB = rOuter + (isMajor ? 8 : 5);
      // Major ticks cycle through 8 pillar colours (12 major ticks → each
      // pillar appears ~1.5 times). Minor ticks stay soft gold.
      const majorIdx = Math.floor(i / 5) % PILLARS.length;
      const colour = isMajor ? PILLARS[majorIdx] : `${GOLD}55`;
      arr.push({
        x1: cx + Math.cos(angle) * rA,
        y1: cy + Math.sin(angle) * rA,
        x2: cx + Math.cos(angle) * rB,
        y2: cy + Math.sin(angle) * rB,
        w: isMajor ? 1.8 : 0.7,
        colour,
        isMajor,
      });
    }
    return arr;
  }, [cx, cy, rOuter]);

  return (
    <div className="relative flex items-center justify-center"
      style={{ width: size, height: size }}>
      {/* ── Breathing coloured aura — pulses the current mode/phase colour
              softly around the ring so the chronograph feels alive. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: -30,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${phaseColor}55 0%, ${phaseColor}22 42%, transparent 72%)`,
          filter: 'blur(22px)',
          animation: 'chBreath 4.2s ease-in-out infinite',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      {/* ── Secondary 8-colour aurora ring — slow rainbow shimmer that
              cycles all pillar colours behind the primary breathing halo. */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          inset: -12,
          borderRadius: '50%',
          background: `conic-gradient(from 0deg, ${PILLARS[0]}44, ${PILLARS[1]}33, ${PILLARS[2]}44, ${PILLARS[3]}33, ${PILLARS[4]}44, ${PILLARS[5]}33, ${PILLARS[6]}44, ${PILLARS[7]}33, ${PILLARS[0]}44)`,
          filter: 'blur(14px)',
          opacity: 0.55,
          animation: 'chAuroraSpin 22s linear infinite',
          pointerEvents: 'none',
          zIndex: 0,
        }}
      />
      <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}
        style={{ position: 'absolute', inset: 0, filter: 'drop-shadow(0 0 24px rgba(212,175,55,0.35))', zIndex: 1 }}>
        <defs>
          <radialGradient id="chGlass" cx="50%" cy="45%" r="55%">
            <stop offset="0%"   stopColor="#141018"/>
            <stop offset="70%"  stopColor="#08060C"/>
            <stop offset="100%" stopColor="#020105"/>
          </radialGradient>
          <linearGradient id="chGold" x1="0" x2="0" y1="0" y2="1">
            <stop offset="0"   stopColor={GOLD_HI}/>
            <stop offset="0.5" stopColor={GOLD}/>
            <stop offset="1"   stopColor={GOLD_LO}/>
          </linearGradient>
        </defs>
        {/* Outer obsidian glass — bezel rotates smoothly while timer is running */}
        <g style={{
          transformOrigin: `${cx}px ${cy}px`,
          animation: running ? 'chBezelSpin 24s linear infinite' : 'none',
        }}>
          <circle cx={cx} cy={cy} r={rOuter + 12} fill="url(#chGlass)" stroke="url(#chGold)" strokeWidth="2"/>
          <circle cx={cx} cy={cy} r={rOuter + 12} fill="none" stroke={`${GOLD_HI}44`} strokeWidth="0.6"/>
          {/* Facet ticks — major ones tinted with the 8 iconic pillar colours */}
          {ticks.map((t, i) => (
            <line key={i} x1={t.x1} y1={t.y1} x2={t.x2} y2={t.y2}
              stroke={t.colour} strokeWidth={t.w} strokeLinecap="round"
              style={t.isMajor ? { filter: `drop-shadow(0 0 4px ${t.colour})` } : undefined}/>
          ))}
        </g>
        {/* Streak arc (outer, dashed) — static */}
        {streakArc > 0 && (
          <circle cx={cx} cy={cy} r={rStreak} fill="none"
            stroke={GOLD_HI} strokeWidth="1.5"
            strokeDasharray={`${streakCirc - streakDash} ${streakCirc}`}
            strokeDashoffset={streakCirc * 0.25}
            transform={`rotate(-90 ${cx} ${cy})`}
            style={{ opacity: 0.65, filter: `drop-shadow(0 0 6px ${GOLD})` }}/>
        )}
        {/* Inner track */}
        <circle cx={cx} cy={cy} r={rInner} fill="none"
          stroke="rgba(255,255,255,0.06)" strokeWidth="3"/>
        {/* Progress arc */}
        <circle cx={cx} cy={cy} r={rInner} fill="none"
          stroke={phaseColor} strokeWidth="3.5" strokeLinecap="round"
          strokeDasharray={`${circ - strokeDash} ${circ}`}
          strokeDashoffset="0"
          transform={`rotate(-90 ${cx} ${cy})`}
          style={{ filter: `drop-shadow(0 0 10px ${phaseColor}) drop-shadow(0 0 20px ${phaseColor}66)`,
            transition: 'stroke 0.4s ease, stroke-dasharray 0.15s linear' }}/>
      </svg>
      <div className="relative flex flex-col items-center justify-center text-center px-4" style={{ zIndex: 2 }}>
        {children}
      </div>
    </div>
  );
};

// ══ MAIN COMPONENT ══════════════════════════════════════════════════
export const TheBlissTimer = ({ onClose }) => {
  // Modes: stopwatch | countdown | interval | pomodoro
  const [mode, setMode] = useState('countdown');
  // Bottom drawer: 'laps' | 'log' | 'ambient' | 'score' | null
  const [drawer, setDrawer] = useState(null);

  // Common
  const [running, setRunning] = useState(false);
  const [muted, setMuted] = useState(false);
  const audioRef = useRef(null);

  // Stopwatch
  const [swElapsed, setSwElapsed] = useState(0);
  const [laps, setLaps] = useState([]);
  const swStartAt = useRef(0);
  const swBase = useRef(0);

  // Countdown
  const [cdDrive, setCdDrive] = useState(25 * 60 * 1000);
  const [cdRemain, setCdRemain] = useState(25 * 60 * 1000);
  const [cdMilestones, setCdMilestones] = useState({ half: false, min1: false, sec10: false });
  const cdEnd = useRef(0);

  // Interval (Tabata)
  const [ivWork, setIvWork] = useState(20);
  const [ivRest, setIvRest] = useState(10);
  const [ivRounds, setIvRounds] = useState(8);
  const [ivRound, setIvRound] = useState(1);
  const [ivPhase, setIvPhase] = useState('work'); // 'work' | 'rest'
  const [ivRemain, setIvRemain] = useState(20 * 1000);
  const ivEnd = useRef(0);

  // Pomodoro
  const [pomoLap, setPomoLap] = useState(0); // 0..3 = work, 4 = long break etc.
  const [pomoPhase, setPomoPhase] = useState('work'); // 'work' | 'break' | 'long'
  const [pomoRemain, setPomoRemain] = useState(25 * 60 * 1000);
  const pomoEnd = useRef(0);

  // Ambient
  const [ambient, setAmbient] = useState('silence');
  const [tickOn, setTickOn] = useState(false);

  // Focus score + streak + log
  const [log, setLog] = useState(() => readLS(LS_LOG, []));
  const [streak, setStreak] = useState(() => readLS(LS_STREAK, { last: null, chain: 0 }));
  const [focusScore, setFocusScore] = useState(() => readLS(LS_SCORE, 0));

  const tickerRef = useRef(null);
  const lastTickRef = useRef(0);

  useHistoryClose(true, useCallback(() => {
    if (drawer) { setDrawer(null); return; }
    onClose();
  }, [drawer, onClose]));

  // Init audio
  useEffect(() => {
    audioRef.current = new ChronographAudio();
    return () => { audioRef.current && audioRef.current.destroy(); };
  }, []);
  useEffect(() => { if (audioRef.current) audioRef.current.setMuted(muted); }, [muted]);
  useEffect(() => { if (audioRef.current) audioRef.current.playAmbient(ambient); }, [ambient]);

  // Focus score daily decay — subtract 2 per missed day, cap 0..100
  useEffect(() => {
    const key = 'blisstimer_score_last_check_v2';
    const last = localStorage.getItem(key);
    const today = todayKey();
    if (last !== today) {
      // Days missed since streak.last
      if (streak.last && streak.last !== today) {
        const from = new Date(streak.last).getTime();
        const to = new Date(today).getTime();
        const missed = Math.max(0, Math.floor((to - from) / 86400000) - 1);
        if (missed > 0) {
          const next = Math.max(0, focusScore - missed * 2);
          setFocusScore(next); writeLS(LS_SCORE, next);
        }
      }
      localStorage.setItem(key, today);
    }
  }, [streak.last, focusScore]);

  // ── Central ticking loop
  useEffect(() => {
    if (!running) { if (tickerRef.current) clearInterval(tickerRef.current); return; }
    tickerRef.current = setInterval(() => {
      const now = Date.now();
      let dispSec = null;
      if (mode === 'stopwatch') {
        const el = swBase.current + (now - swStartAt.current);
        dispSec = Math.floor(el / 1000);
        setSwElapsed(el);
      } else if (mode === 'countdown') {
        const left = Math.max(0, cdEnd.current - now);
        dispSec = Math.ceil(left / 1000);
        setCdRemain(left);
        // Milestones
        const total = cdDrive;
        if (!cdMilestones.half && left <= total / 2 && total > 4000) {
          setCdMilestones((m) => ({ ...m, half: true }));
          audioRef.current && audioRef.current.milestone();
        }
        if (!cdMilestones.min1 && left <= 60000 && total > 120000) {
          setCdMilestones((m) => ({ ...m, min1: true }));
          audioRef.current && audioRef.current.milestone();
        }
        if (!cdMilestones.sec10 && left <= 10000 && left > 0) {
          setCdMilestones((m) => ({ ...m, sec10: true }));
          audioRef.current && audioRef.current.milestone();
        }
        if (left <= 0) {
          finishCountdown();
        }
      } else if (mode === 'interval') {
        const left = Math.max(0, ivEnd.current - now);
        dispSec = Math.ceil(left / 1000);
        setIvRemain(left);
        if (left <= 0) advanceInterval();
      } else if (mode === 'pomodoro') {
        const left = Math.max(0, pomoEnd.current - now);
        dispSec = Math.ceil(left / 1000);
        setPomoRemain(left);
        if (left <= 0) advancePomodoro();
      }
      // Tick exactly when the displayed second changes (audio = visual).
      if (tickOn && dispSec !== null && dispSec !== lastTickRef.current) {
        lastTickRef.current = dispSec;
        audioRef.current && audioRef.current.tick();
      }
    }, 90);
    return () => clearInterval(tickerRef.current);
  }, [running, mode, cdDrive, cdMilestones, tickOn]);

  // ── Helpers: award session
  const recordSession = (kind, ms) => {
    const entry = { at: Date.now(), kind, ms, note: '' };
    const next = [entry, ...log].slice(0, 30);
    setLog(next); writeLS(LS_LOG, next);
    // Streak
    const today = todayKey();
    let nextStreak = streak;
    if (streak.last === today) {
      // already counted today
    } else {
      const yesterday = new Date(Date.now() - 86400000); const y = todayKey.call({}) && `${yesterday.getFullYear()}-${String(yesterday.getMonth()+1).padStart(2,'0')}-${String(yesterday.getDate()).padStart(2,'0')}`;
      nextStreak = { last: today, chain: (streak.last === y ? streak.chain + 1 : 1) };
      setStreak(nextStreak); writeLS(LS_STREAK, nextStreak);
    }
    // Focus score +5 per session, cap 100
    const nextScore = Math.min(100, focusScore + 5);
    setFocusScore(nextScore); writeLS(LS_SCORE, nextScore);
  };

  // ── Mode: countdown
  const startCountdown = () => {
    setRunning(true); setCdMilestones({ half: false, min1: false, sec10: false });
    cdEnd.current = Date.now() + cdRemain;
    audioRef.current && audioRef.current._ensure();
    audioRef.current && audioRef.current.startChime();
  };
  const pauseCountdown = () => {
    if (running) { setCdRemain(Math.max(0, cdEnd.current - Date.now())); }
    setRunning(false);
    audioRef.current && audioRef.current.stopChime();
  };
  const resetCountdown = () => { setRunning(false); setCdRemain(cdDrive); setCdMilestones({ half: false, min1: false, sec10: false }); };
  const finishCountdown = () => {
    setRunning(false); setCdRemain(0);
    audioRef.current && audioRef.current.fanfare();
    recordSession('countdown', cdDrive);
  };

  // ── Mode: stopwatch
  const startStopwatch = () => {
    swStartAt.current = Date.now();
    setRunning(true);
    audioRef.current && audioRef.current._ensure();
    audioRef.current && audioRef.current.startChime();
  };
  const stopStopwatch = () => {
    swBase.current = swElapsed;
    setRunning(false);
    audioRef.current && audioRef.current.stopChime();
  };
  const resetStopwatch = () => { setRunning(false); setSwElapsed(0); swBase.current = 0; setLaps([]); };
  const lapStopwatch = () => {
    if (!running) return;
    setLaps((prev) => [{ at: Date.now(), ms: swElapsed, idx: prev.length + 1 }, ...prev]);
    audioRef.current && audioRef.current.tick();
  };

  // ── Mode: interval (Tabata)
  const startInterval = () => {
    setIvRound(1); setIvPhase('work'); setIvRemain(ivWork * 1000);
    ivEnd.current = Date.now() + ivWork * 1000;
    setRunning(true);
    audioRef.current && audioRef.current._ensure();
    audioRef.current && audioRef.current.startChime();
  };
  const pauseInterval = () => { setIvRemain(Math.max(0, ivEnd.current - Date.now())); setRunning(false); };
  const resetInterval = () => { setRunning(false); setIvRound(1); setIvPhase('work'); setIvRemain(ivWork * 1000); };
  const advanceInterval = () => {
    audioRef.current && audioRef.current.milestone();
    if (ivPhase === 'work') {
      setIvPhase('rest'); setIvRemain(ivRest * 1000);
      ivEnd.current = Date.now() + ivRest * 1000;
    } else {
      // Rest ended → next round
      if (ivRound >= ivRounds) {
        setRunning(false);
        audioRef.current && audioRef.current.fanfare();
        recordSession('interval', ivRounds * (ivWork + ivRest) * 1000);
        return;
      }
      setIvRound((r) => r + 1); setIvPhase('work'); setIvRemain(ivWork * 1000);
      ivEnd.current = Date.now() + ivWork * 1000;
    }
  };

  // ── Mode: pomodoro
  const startPomodoro = () => {
    setPomoLap(0); setPomoPhase('work'); setPomoRemain(25 * 60 * 1000);
    pomoEnd.current = Date.now() + 25 * 60 * 1000;
    setRunning(true);
    audioRef.current && audioRef.current._ensure();
    audioRef.current && audioRef.current.startChime();
  };
  const pausePomodoro = () => { setPomoRemain(Math.max(0, pomoEnd.current - Date.now())); setRunning(false); };
  const resetPomodoro = () => { setRunning(false); setPomoLap(0); setPomoPhase('work'); setPomoRemain(25 * 60 * 1000); };
  const advancePomodoro = () => {
    audioRef.current && audioRef.current.milestone();
    if (pomoPhase === 'work') {
      recordSession('pomodoro', 25 * 60 * 1000);
      // Every 4th work → long break, else short break
      const nextLap = pomoLap + 1;
      if (nextLap >= 4) {
        setPomoLap(0); setPomoPhase('long'); setPomoRemain(15 * 60 * 1000);
        pomoEnd.current = Date.now() + 15 * 60 * 1000;
      } else {
        setPomoLap(nextLap); setPomoPhase('break'); setPomoRemain(5 * 60 * 1000);
        pomoEnd.current = Date.now() + 5 * 60 * 1000;
      }
    } else {
      setPomoPhase('work'); setPomoRemain(25 * 60 * 1000);
      pomoEnd.current = Date.now() + 25 * 60 * 1000;
    }
  };

  // ── Derived: progress / display
  let progress = 0; let display = '00:00'; let phaseColor = GOLD;
  let subtitle = '';
  if (mode === 'stopwatch') {
    progress = (swElapsed % 60000) / 60000; // fills every minute
    display = fmtDeci(swElapsed);
    subtitle = running ? 'IN MOTION' : (swElapsed > 0 ? 'PAUSED' : 'AWAITING');
    phaseColor = PILLAR_CYAN;
  } else if (mode === 'countdown') {
    progress = cdDrive > 0 ? 1 - (cdRemain / cdDrive) : 0;
    display = fmt(cdRemain);
    subtitle = running ? 'IN FLIGHT' : (cdRemain === cdDrive ? 'PRIMED' : cdRemain === 0 ? 'COMPLETE' : 'PAUSED');
    phaseColor = cdRemain <= 10000 && running ? PILLAR_CRIMSON : (cdRemain <= 60000 && running ? PILLAR_YELLOW : GOLD_HI);
  } else if (mode === 'interval') {
    const total = (ivPhase === 'work' ? ivWork : ivRest) * 1000;
    progress = total > 0 ? 1 - (ivRemain / total) : 0;
    display = fmt(ivRemain);
    subtitle = `${ivPhase.toUpperCase()} · ROUND ${ivRound}/${ivRounds}`;
    phaseColor = ivPhase === 'work' ? PILLAR_CRIMSON : PILLAR_EMERALD;
  } else if (mode === 'pomodoro') {
    const total = pomoPhase === 'work' ? 25 * 60 * 1000 : pomoPhase === 'long' ? 15 * 60 * 1000 : 5 * 60 * 1000;
    progress = 1 - (pomoRemain / total);
    display = fmt(pomoRemain);
    subtitle = `${pomoPhase === 'work' ? 'FOCUS' : pomoPhase === 'long' ? 'LONG REST' : 'BREAK'} · LAP ${pomoLap + 1}/4`;
    phaseColor = pomoPhase === 'work' ? PILLAR_VIOLET : PILLAR_EMERALD;
  }

  // Streak arc = min(streak.chain / 7, 1)
  const streakArc = Math.min(1, streak.chain / 7);

  const modes = [
    { id: 'countdown', label: 'Timer',    Icon: Clock,  pillar: PILLAR_GOLD },
    { id: 'stopwatch', label: 'Stopwatch',Icon: Play,   pillar: PILLAR_CYAN },
    { id: 'interval',  label: 'Interval', Icon: Zap,    pillar: PILLAR_CRIMSON },
    { id: 'pomodoro',  label: 'Pomodoro', Icon: Layers, pillar: PILLAR_VIOLET },
  ];

  return (
    <div className="fixed inset-0 z-[220] flex flex-col overflow-hidden"
      style={{ background: `radial-gradient(ellipse at top, #100812 0%, ${OBSIDIAN} 60%, #000 100%)` }}
      data-testid="blisstimer-modal">
      <style>{`
        @keyframes chAurora {
          0%,100% { transform: translate(0,0) scale(1); opacity: 0.35; }
          50%     { transform: translate(-10px, 8px) scale(1.05); opacity: 0.55; }
        }
        @keyframes chBezelSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes chDust {
          0%   { transform: translateY(0) translateX(0); opacity: 0; }
          10%  { opacity: 0.9; }
          100% { transform: translateY(-90vh) translateX(20px); opacity: 0; }
        }
        @keyframes chBreath {
          0%,100% { transform: scale(1);    opacity: 0.55; }
          50%     { transform: scale(1.10); opacity: 0.95; }
        }
        @keyframes chAuroraSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .ch-digits {
          font-family: 'Courier New', 'Roboto Mono', monospace;
          font-weight: 800;
          letter-spacing: 0.02em;
          -webkit-text-stroke: 0.6px rgba(255,233,160,0.25);
        }
      `}</style>

      {/* Faint aurora particles behind ring — four gentle orbs cycling
          through emerald, sapphire, violet, and crimson so the backdrop
          mirrors the iconic pillar palette. */}
      <div className="absolute pointer-events-none" style={{ inset: 0, overflow: 'hidden' }}>
        <div style={{
          position: 'absolute', left: '8%', top: '18%',
          width: 200, height: 200, borderRadius: '50%',
          background: `radial-gradient(circle, ${PILLAR_EMERALD}22, transparent 60%)`,
          filter: 'blur(34px)', animation: 'chAurora 12s ease-in-out infinite',
        }}/>
        <div style={{
          position: 'absolute', right: '6%', top: '22%',
          width: 240, height: 240, borderRadius: '50%',
          background: `radial-gradient(circle, ${PILLAR_SAPPH}22, transparent 60%)`,
          filter: 'blur(38px)', animation: 'chAurora 14s ease-in-out infinite reverse',
        }}/>
        <div style={{
          position: 'absolute', left: '15%', bottom: '18%',
          width: 220, height: 220, borderRadius: '50%',
          background: `radial-gradient(circle, ${PILLAR_VIOLET}20, transparent 60%)`,
          filter: 'blur(36px)', animation: 'chAurora 16s ease-in-out infinite',
        }}/>
        <div style={{
          position: 'absolute', right: '10%', bottom: '20%',
          width: 200, height: 200, borderRadius: '50%',
          background: `radial-gradient(circle, ${PILLAR_CRIMSON}1D, transparent 60%)`,
          filter: 'blur(30px)', animation: 'chAurora 13s ease-in-out infinite reverse',
        }}/>
      </div>

      {/* Header */}
      <div className="flex-shrink-0 flex items-center justify-between px-3 py-2.5 border-b relative"
        style={{ borderColor: `${GOLD}33`, background: `${OBSIDIAN}CC`, backdropFilter: 'blur(8px)' }}>
        <BackPill onClick={onClose} accent="#00A6FF" testId="blisstimer-back"/>
        <p className="font-['Cinzel'] uppercase tracking-[0.32em]"
          style={{ color: GOLD_HI, fontSize: 13, textShadow: `0 0 12px ${GOLD}88` }}>
          CHRONOGRAPH
        </p>
        <button onClick={() => setMuted((m) => !m)} data-testid="blisstimer-mute"
          className="w-8 h-8 rounded-full flex items-center justify-center"
          style={{ background: `${GOLD}18`, border: `1px solid ${GOLD}66`, color: GOLD_HI }}>
          {muted ? <VolumeX size={13}/> : <Volume2 size={13}/>}
        </button>
      </div>

      {/* Mode pills — fit all 4 in one row without horizontal scroll */}
      <div className="flex-shrink-0 flex items-stretch gap-1 px-2 py-2"
        style={{ background: `${OBSIDIAN}AA`, borderBottom: `1px solid ${GOLD}22` }}
        data-testid="blisstimer-mode-pills">
        {modes.map((m) => {
          const active = mode === m.id;
          return (
            <button key={m.id}
              type="button"
              onClick={() => { setMode(m.id); setRunning(false); }}
              data-testid={`blisstimer-mode-${m.id}`}
              className="flex-1 min-w-0 flex flex-col items-center justify-center gap-0.5 py-1.5 rounded-full active:scale-95 transition-transform"
              style={{
                background: active
                  ? `linear-gradient(135deg, ${m.pillar}55, ${m.pillar}18)`
                  : 'rgba(255,255,255,0.03)',
                border: `1.5px solid ${active ? m.pillar : `${GOLD}44`}`,
                boxShadow: active ? `0 0 10px ${m.pillar}88` : 'none',
              }}>
              <m.Icon size={11} style={{ color: active ? m.pillar : `${GOLD_HI}AA` }}/>
              <span className="font-['Outfit'] uppercase tracking-[0.10em] leading-none"
                style={{
                  fontSize: 9,
                  color: active ? '#FFFFFF' : `${GOLD_HI}CC`,
                  textShadow: active ? `0 0 6px ${m.pillar}` : 'none',
                }}>
                {m.label}
              </span>
            </button>
          );
        })}
      </div>

      {/* Main chronograph */}
      <div className="flex-1 flex flex-col items-center justify-center relative px-3 min-h-0">
        <ChronographRing size={280} progress={progress} phaseColor={phaseColor} streakArc={streakArc} running={running}>
          <p className="font-['Outfit'] uppercase tracking-[0.32em] mb-2"
            style={{ color: `${GOLD}CC`, fontSize: 8 }}
            data-testid="blisstimer-subtitle">
            {subtitle}
          </p>
          <p className="ch-digits"
            style={{ color: phaseColor,
              fontSize: mode === 'stopwatch' ? 46 : 56,
              lineHeight: 1,
              textShadow: `0 0 20px ${phaseColor}66, 0 0 40px ${phaseColor}33` }}
            data-testid="blisstimer-display">
            {display}
          </p>
          <div className="mt-3 flex items-center gap-2 font-['Outfit'] uppercase tracking-[0.22em]"
            style={{ color: `${GOLD}99`, fontSize: 8 }}>
            <span>Streak · <span style={{ color: GOLD_HI }}>{streak.chain}d</span></span>
            <span className="opacity-40">·</span>
            <span>Focus · <span style={{ color: GOLD_HI }}>{focusScore}</span></span>
          </div>
        </ChronographRing>

        {/* Mode-specific presets/controls */}
        {mode === 'countdown' && !running && (
          <div className="mt-3 flex items-center gap-1.5 flex-wrap justify-center"
            data-testid="blisstimer-preset-row">
            {[5, 15, 25, 45, 60].map((mins) => {
              const active = cdDrive === mins * 60000;
              return (
                <button key={mins}
                  type="button"
                  onClick={() => { setCdDrive(mins * 60000); setCdRemain(mins * 60000); }}
                  data-testid={`blisstimer-preset-${mins}`}
                  className="px-3 py-1.5 rounded-full active:scale-95 transition-transform"
                  style={{
                    background: active ? `linear-gradient(135deg, ${GOLD_HI}, ${GOLD})` : 'transparent',
                    color: active ? OBSIDIAN : GOLD,
                    border: `1px solid ${active ? GOLD_HI : `${GOLD}55`}`,
                  }}>
                  <span className="font-['Outfit'] uppercase" style={{ fontSize: 10, letterSpacing: '0.18em' }}>
                    {mins} MIN
                  </span>
                </button>
              );
            })}
          </div>
        )}

        {mode === 'interval' && !running && (
          <div className="mt-3 flex flex-col items-center gap-2" data-testid="blisstimer-interval-config">
            <p className="font-['Outfit'] uppercase tracking-[0.22em]" style={{ color: `${GOLD}88`, fontSize: 8 }}>
              Work · Rest · Rounds
            </p>
            <div className="flex items-center gap-2">
              <IntervalKnob value={ivWork} onChange={setIvWork} min={5} max={60} suffix="s"/>
              <IntervalKnob value={ivRest} onChange={setIvRest} min={5} max={60} suffix="s"/>
              <IntervalKnob value={ivRounds} onChange={setIvRounds} min={2} max={20} suffix="×"/>
            </div>
          </div>
        )}

        {mode === 'pomodoro' && (
          <div className="mt-3 flex items-center gap-1" data-testid="blisstimer-pomo-chain">
            {[0, 1, 2, 3].map((i) => (
              <span key={i}
                className="w-3 h-3 rounded-full"
                style={{
                  background: i < pomoLap ? GOLD_HI : (i === pomoLap && pomoPhase === 'work' ? GOLD : 'transparent'),
                  border: `1.5px solid ${GOLD}88`,
                  boxShadow: i < pomoLap ? `0 0 8px ${GOLD}` : 'none',
                }}/>
            ))}
            <span className="ml-2 font-['Outfit'] uppercase tracking-[0.22em]"
              style={{ color: `${GOLD}CC`, fontSize: 9 }}>
              25 · 5 · 25 · 5 · 25 · 5 · 25 · 15
            </span>
          </div>
        )}
      </div>

      {/* Primary controls */}
      <div className="flex-shrink-0 px-4 py-3 flex items-center justify-center gap-3">
        {(mode === 'countdown' && !running) && (
          <button onClick={startCountdown} data-testid="blisstimer-start"
            className="flex items-center gap-2 px-6 py-3 rounded-full active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD})`,
              color: OBSIDIAN, boxShadow: `0 0 20px ${GOLD}77`,
              border: `1.5px solid ${GOLD_HI}`,
            }}>
            <Play size={13}/>
            <span className="font-['Outfit'] uppercase tracking-[0.24em]" style={{ fontSize: 11 }}>Start Drive</span>
          </button>
        )}
        {(mode === 'countdown' && running) && (
          <>
            <button onClick={pauseCountdown} data-testid="blisstimer-pause"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full active:scale-95"
              style={{ background: `${GOLD}22`, color: GOLD_HI, border: `1.5px solid ${GOLD}` }}>
              <Pause size={12}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]" style={{ fontSize: 10 }}>Pause</span>
            </button>
            <button onClick={resetCountdown} data-testid="blisstimer-reset"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full active:scale-95"
              style={{ background: 'transparent', color: '#E85D5D', border: `1.5px solid #E85D5D` }}>
              <RotateCcw size={12}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]" style={{ fontSize: 10 }}>Reset</span>
            </button>
          </>
        )}
        {(mode === 'stopwatch' && !running) && (
          <>
            <button onClick={startStopwatch} data-testid="blisstimer-start"
              className="flex items-center gap-2 px-6 py-3 rounded-full active:scale-95"
              style={{
                background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD})`,
                color: OBSIDIAN, boxShadow: `0 0 20px ${GOLD}77`,
                border: `1.5px solid ${GOLD_HI}`,
              }}>
              <Play size={13}/>
              <span className="font-['Outfit'] uppercase tracking-[0.24em]" style={{ fontSize: 11 }}>{swElapsed === 0 ? 'Start' : 'Resume'}</span>
            </button>
            {swElapsed > 0 && (
              <button onClick={resetStopwatch} data-testid="blisstimer-reset"
                className="flex items-center gap-2 px-5 py-2.5 rounded-full active:scale-95"
                style={{ background: 'transparent', color: '#E85D5D', border: `1.5px solid #E85D5D` }}>
                <RotateCcw size={12}/>
                <span className="font-['Outfit'] uppercase tracking-[0.22em]" style={{ fontSize: 10 }}>Reset</span>
              </button>
            )}
          </>
        )}
        {(mode === 'stopwatch' && running) && (
          <>
            <button onClick={stopStopwatch} data-testid="blisstimer-pause"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full active:scale-95"
              style={{ background: `${GOLD}22`, color: GOLD_HI, border: `1.5px solid ${GOLD}` }}>
              <Pause size={12}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]" style={{ fontSize: 10 }}>Stop</span>
            </button>
            <button onClick={lapStopwatch} data-testid="blisstimer-lap"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full active:scale-95"
              style={{ background: 'transparent', color: GOLD_HI, border: `1.5px solid ${GOLD}88` }}>
              <Flag size={12}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]" style={{ fontSize: 10 }}>Lap</span>
            </button>
          </>
        )}
        {(mode === 'interval' && !running) && (
          <button onClick={startInterval} data-testid="blisstimer-start"
            className="flex items-center gap-2 px-6 py-3 rounded-full active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD})`,
              color: OBSIDIAN, boxShadow: `0 0 20px ${GOLD}77`,
            }}>
            <Play size={13}/>
            <span className="font-['Outfit'] uppercase tracking-[0.24em]" style={{ fontSize: 11 }}>Begin Interval</span>
          </button>
        )}
        {(mode === 'interval' && running) && (
          <>
            <button onClick={pauseInterval} data-testid="blisstimer-pause"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full active:scale-95"
              style={{ background: `${GOLD}22`, color: GOLD_HI, border: `1.5px solid ${GOLD}` }}>
              <Pause size={12}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]" style={{ fontSize: 10 }}>Pause</span>
            </button>
            <button onClick={resetInterval} data-testid="blisstimer-reset"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full active:scale-95"
              style={{ background: 'transparent', color: '#E85D5D', border: `1.5px solid #E85D5D` }}>
              <RotateCcw size={12}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]" style={{ fontSize: 10 }}>Reset</span>
            </button>
          </>
        )}
        {(mode === 'pomodoro' && !running) && (
          <button onClick={startPomodoro} data-testid="blisstimer-start"
            className="flex items-center gap-2 px-6 py-3 rounded-full active:scale-95"
            style={{
              background: `linear-gradient(135deg, ${GOLD_HI}, ${GOLD})`,
              color: OBSIDIAN, boxShadow: `0 0 20px ${GOLD}77`,
            }}>
            <Play size={13}/>
            <span className="font-['Outfit'] uppercase tracking-[0.24em]" style={{ fontSize: 11 }}>Begin Chain</span>
          </button>
        )}
        {(mode === 'pomodoro' && running) && (
          <>
            <button onClick={pausePomodoro} data-testid="blisstimer-pause"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full active:scale-95"
              style={{ background: `${GOLD}22`, color: GOLD_HI, border: `1.5px solid ${GOLD}` }}>
              <Pause size={12}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]" style={{ fontSize: 10 }}>Pause</span>
            </button>
            <button onClick={resetPomodoro} data-testid="blisstimer-reset"
              className="flex items-center gap-2 px-5 py-2.5 rounded-full active:scale-95"
              style={{ background: 'transparent', color: '#E85D5D', border: `1.5px solid #E85D5D` }}>
              <RotateCcw size={12}/>
              <span className="font-['Outfit'] uppercase tracking-[0.22em]" style={{ fontSize: 10 }}>Reset</span>
            </button>
          </>
        )}
      </div>

      {/* Bottom tab bar — one-tap access to Laps · Log · Ambient · Score.
          Pinned to viewport bottom with safe-area padding so all four tabs
          stay fully visible on every device (never half-hidden). */}
      <div className="flex-shrink-0 flex border-t px-2 pt-1.5"
        style={{
          borderColor: `${GOLD}33`,
          background: `${OBSIDIAN}EE`,
          backdropFilter: 'blur(8px)',
          paddingBottom: 'max(6px, var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))',
          minHeight: 62,
          position: 'relative',
          zIndex: 50,
        }}
        data-testid="blisstimer-tabs">
        <TabBtn label="Laps"    Icon={Flag}  onClick={() => setDrawer((d) => d === 'laps' ? null : 'laps')}    active={drawer === 'laps'}    testId="tab-laps"    pillar={PILLAR_CYAN}/>
        <TabBtn label="Log"     Icon={List}  onClick={() => setDrawer((d) => d === 'log' ? null : 'log')}     active={drawer === 'log'}     testId="tab-log"     pillar={PILLAR_SAPPH}/>
        <TabBtn label="Ambient" Icon={Cloud} onClick={() => setDrawer((d) => d === 'ambient' ? null : 'ambient')} active={drawer === 'ambient'} testId="tab-ambient" pillar={PILLAR_EMERALD}/>
        <TabBtn label="Score"   Icon={Zap}   onClick={() => setDrawer((d) => d === 'score' ? null : 'score')} active={drawer === 'score'}   testId="tab-score"   pillar={PILLAR_VIOLET}/>
      </div>

      {/* Drawer */}
      {drawer === 'laps' && (
        <Drawer title="Splits" onClose={() => setDrawer(null)} testId="drawer-laps">
          {laps.length === 0 ? (
            <p className="italic font-['Cormorant_Garamond'] text-center py-8" style={{ color: `${GOLD}88` }}>
              No laps yet. Tap &ldquo;Lap&rdquo; while the stopwatch runs.
            </p>
          ) : (
            <div className="space-y-1.5">
              {laps.map((l, i) => {
                const prev = laps[i + 1];
                const split = prev ? l.ms - prev.ms : l.ms;
                return (
                  <div key={l.at} className="flex justify-between items-center px-3 py-2 rounded-lg"
                    style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${GOLD}22` }}>
                    <span className="font-['Outfit'] uppercase tracking-[0.22em]" style={{ color: `${GOLD}AA`, fontSize: 10 }}>Lap {l.idx}</span>
                    <span className="ch-digits" style={{ color: GOLD_HI, fontSize: 14 }}>{fmtDeci(split)}</span>
                    <span className="ch-digits" style={{ color: `${GOLD}AA`, fontSize: 12 }}>{fmtDeci(l.ms)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Drawer>
      )}
      {drawer === 'log' && (
        <Drawer title="Session Log" onClose={() => setDrawer(null)} testId="drawer-log">
          {log.length === 0 ? (
            <p className="italic font-['Cormorant_Garamond'] text-center py-8" style={{ color: `${GOLD}88` }}>
              No sessions recorded yet. Complete a countdown, interval or pomodoro to log it here.
            </p>
          ) : (
            <div className="space-y-1.5">
              {log.map((e) => {
                const d = new Date(e.at);
                const when = `${d.toLocaleDateString()} ${d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}`;
                return (
                  <div key={e.at} className="flex justify-between items-center px-3 py-2 rounded-lg"
                    style={{ background: 'rgba(255,255,255,0.02)', border: `1px solid ${GOLD}22` }}>
                    <div>
                      <p className="font-['Outfit'] uppercase tracking-[0.22em]" style={{ color: GOLD_HI, fontSize: 10 }}>{e.kind}</p>
                      <p className="font-['Cormorant_Garamond']" style={{ color: `${GOLD}88`, fontSize: 11 }}>{when}</p>
                    </div>
                    <span className="ch-digits" style={{ color: GOLD_HI, fontSize: 14 }}>{fmt(e.ms)}</span>
                  </div>
                );
              })}
            </div>
          )}
        </Drawer>
      )}
      {drawer === 'ambient' && (
        <Drawer title="Ambience" onClose={() => setDrawer(null)} testId="drawer-ambient">
          <div className="grid grid-cols-3 gap-1.5">
            {[
              { id: 'silence', label: 'Silence' },
              { id: 'rain',    label: 'Rain'    },
              { id: 'ocean',   label: 'Ocean'   },
              { id: 'cafe',    label: 'Cafe'    },
              { id: 'bonfire', label: 'Bonfire' },
              { id: 'energy',  label: 'Energy'  },
            ].map((a) => (
              <button key={a.id}
                type="button"
                onClick={() => setAmbient(a.id)}
                data-testid={`ambient-${a.id}`}
                className="py-2 rounded-lg active:scale-95"
                style={{
                  background: ambient === a.id ? `linear-gradient(135deg, ${GOLD}22, ${GOLD}08)` : 'rgba(255,255,255,0.02)',
                  border: `1.5px solid ${ambient === a.id ? GOLD : `${GOLD}33`}`,
                  boxShadow: ambient === a.id ? `0 0 10px ${GOLD}55` : 'none',
                }}>
                <span className="font-['Outfit'] uppercase tracking-[0.18em]"
                  style={{ color: ambient === a.id ? GOLD_HI : `${GOLD}AA`, fontSize: 9 }}>
                  {a.label}
                </span>
              </button>
            ))}
          </div>
          <label className="mt-2 flex items-center gap-2 cursor-pointer">
            <input type="checkbox" checked={tickOn} onChange={(e) => setTickOn(e.target.checked)}
              data-testid="ambient-tick-toggle"/>
            <span className="font-['Outfit'] uppercase tracking-[0.22em]"
              style={{ color: `${GOLD}CC`, fontSize: 10 }}>
              Second tick
            </span>
          </label>
        </Drawer>
      )}
      {drawer === 'score' && (
        <Drawer title="Focus Score" onClose={() => setDrawer(null)} testId="drawer-score">
          <div className="text-center py-2">
            <p className="ch-digits" style={{ color: GOLD_HI, fontSize: 56, textShadow: `0 0 30px ${GOLD}` }}
              data-testid="focus-score-value">
              {focusScore}
            </p>
            <p className="font-['Outfit'] uppercase tracking-[0.28em]" style={{ color: `${GOLD}88`, fontSize: 9 }}>
              / 100 · IMPERIAL RATING
            </p>
            <div className="mt-4 h-2 rounded-full overflow-hidden"
              style={{ background: 'rgba(255,255,255,0.05)', border: `1px solid ${GOLD}33` }}>
              <div style={{ width: `${focusScore}%`, height: '100%',
                background: `linear-gradient(90deg, ${GOLD_LO}, ${GOLD_HI})`,
                boxShadow: `0 0 8px ${GOLD}` }}/>
            </div>
            <p className="mt-4 font-['Cormorant_Garamond'] italic" style={{ color: `${GOLD}AA`, fontSize: 12 }}>
              +5 per completed session · −2 per missed day · Streak: {streak.chain} day{streak.chain === 1 ? '' : 's'}
            </p>
          </div>
        </Drawer>
      )}
    </div>
  );
};

// ── Small helper components ────────────────────────────────────────
const TabBtn = ({ label, Icon, onClick, active, testId, pillar }) => (
  <button
    type="button"
    onClick={onClick}
    data-testid={testId}
    className="flex-1 flex flex-col items-center gap-0.5 py-1.5 rounded-lg active:scale-95"
    style={{
      background: active ? `${pillar}22` : 'transparent',
      border: `1.5px solid ${active ? pillar : 'transparent'}`,
      color: active ? '#FFFFFF' : `${pillar}AA`,
      boxShadow: active ? `0 0 10px ${pillar}66` : 'none',
    }}>
    <Icon size={13} style={{ color: active ? pillar : undefined }}/>
    <span className="font-['Outfit'] uppercase tracking-[0.16em]"
      style={{ fontSize: 9, textShadow: active ? `0 0 6px ${pillar}` : 'none' }}>
      {label}
    </span>
  </button>
);

const IntervalKnob = ({ value, onChange, min, max, suffix = '' }) => (
  <div className="flex flex-col items-center rounded-lg px-2 py-1.5"
    style={{ background: 'rgba(255,255,255,0.03)', border: `1px solid ${GOLD}44` }}>
    <span className="font-['Outfit'] uppercase tracking-[0.2em]" style={{ color: `${GOLD}88`, fontSize: 8 }}>
      {suffix === '×' ? 'Rounds' : (suffix === 's' && value < 30 ? 'Sec' : 'Sec')}
    </span>
    <div className="flex items-center gap-1.5 mt-1">
      <button type="button" onClick={() => onChange(Math.max(min, value - (suffix === '×' ? 1 : 5)))}
        className="w-5 h-5 rounded flex items-center justify-center"
        style={{ background: `${GOLD}22`, color: GOLD_HI, fontSize: 12 }}>−</button>
      <span className="ch-digits" style={{ color: GOLD_HI, fontSize: 16, minWidth: 28, textAlign: 'center' }}>
        {value}{suffix === '×' ? '' : ''}
      </span>
      <button type="button" onClick={() => onChange(Math.min(max, value + (suffix === '×' ? 1 : 5)))}
        className="w-5 h-5 rounded flex items-center justify-center"
        style={{ background: `${GOLD}22`, color: GOLD_HI, fontSize: 12 }}>+</button>
    </div>
  </div>
);

const Drawer = ({ title, onClose, children, testId }) => (
  <div
    className="absolute left-0 right-0 mx-2 rounded-2xl flex flex-col"
    style={{
      // Sit just above the ~62px bottom tab bar (+ safe-area). Never grows
      // taller than 26vh so it can't overlap the central chronograph ring.
      bottom: 'calc(66px + var(--safe-area-inset-bottom, env(safe-area-inset-bottom, 0px)))',
      height: '26vh',
      maxHeight: 240,
      minHeight: 160,
      background: `linear-gradient(180deg, ${NAVY_HI}, ${OBSIDIAN})`,
      border: `1.5px solid ${GOLD}66`,
      boxShadow: `0 -10px 30px rgba(0,0,0,0.6), 0 0 30px ${GOLD}33`,
      zIndex: 45,
    }}
    data-testid={testId}>
    <div className="flex-shrink-0 flex items-center justify-between px-3 pt-3 pb-2">
      <p className="font-['Cinzel'] uppercase tracking-[0.22em]" style={{ color: GOLD_HI, fontSize: 11 }}>
        {title}
      </p>
      <button onClick={onClose} className="w-6 h-6 rounded-full flex items-center justify-center"
        style={{ border: `1px solid ${GOLD}55`, color: GOLD_HI, fontSize: 12 }}>
        ×
      </button>
    </div>
    {/* Scrollable content — data grows vertically inside this fixed frame */}
    <div className="flex-1 min-h-0 overflow-y-auto overscroll-contain px-3 pb-3"
      style={{ WebkitOverflowScrolling: 'touch' }}>
      {children}
    </div>
  </div>
);

/* ── Chronograph SVG tile icon ── */
export const BlissTimerTileIcon = ({ size = 26 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24"
    style={{ filter: 'drop-shadow(0 0 10px rgba(212,175,55,0.55))' }}>
    <defs>
      <linearGradient id="btGold2" x1="0" x2="0" y1="0" y2="1">
        <stop offset="0"   stopColor="#FFE9A0"/>
        <stop offset="0.6" stopColor="#D4AF37"/>
        <stop offset="1"   stopColor="#8B6B1F"/>
      </linearGradient>
    </defs>
    {/* Watch bezel */}
    <circle cx="12" cy="12" r="9.5" fill="#0a0810" stroke="url(#btGold2)" strokeWidth="1.4"/>
    {/* Facet ticks */}
    {[0, 3, 6, 9].map((h) => {
      const a = h * (Math.PI / 2) - Math.PI / 2;
      return (
        <line key={h} x1={12 + Math.cos(a) * 7.5} y1={12 + Math.sin(a) * 7.5}
              x2={12 + Math.cos(a) * 9} y2={12 + Math.sin(a) * 9}
              stroke="#FFE9A0" strokeWidth="1.2" strokeLinecap="round"/>
      );
    })}
    {/* Hands */}
    <line x1="12" y1="12" x2="12" y2="6.5" stroke="#FFE9A0" strokeWidth="1.4" strokeLinecap="round"/>
    <line x1="12" y1="12" x2="16.5" y2="12" stroke="#D4AF37" strokeWidth="1.1" strokeLinecap="round"/>
    <circle cx="12" cy="12" r="1.2" fill="url(#btGold2)"/>
  </svg>
);

export default TheBlissTimer;
